import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { ButtonComponent } from '@lx/shared/ui/button';
import { LoadingService } from '@lx/shared/ui/loading-spinner';
import { BreakpointObserverService } from '@lx/shared/utils-dom';
import { LoggerService } from '@lx/shared/logger';
import { FlywireGatewayV2Service, PAYMENT_GATEWAY_V2_SERVICE } from '@lx/shared/payment';
import { QuoteLeadOutputV5 } from '@lx/quote-tool/shared/types';
import { CardModule } from 'primeng/card';
import { first, switchMap } from 'rxjs';
import { LeadService, NavigationService } from '../../../services';
import { OnlineQuoteToolStore } from '../../../store';
import { RouteIds } from '../../../types';
import { CourseSummaryComponent } from '../../course-summary/course-summary.component';
import { LayoutComponent } from '../../layout/layout.component';
import { EnrolmentFormComponent } from '../../enrolment-form/enrolment-form.component';
import { customEmailValidator, minPhoneNumberLength } from '@lx/quote-tool/lite/shared/utils';
import { PricePipe } from '@lx/shared/ui/pipes';
import { MINIMUM_PHONE_LENGTH } from '@lx/quote-tool/shared/validators';
import { GoogleAnalyticsClickEventDirective } from '@lx/shared/google-analytics';
import { GoogleAnalyticsEventEnum } from '../../../google-analytics';

@Component({
  standalone: true,
  selector: 'lx-your-information',
  templateUrl: './your-information.component.html',
  styleUrls: ['./your-information.component.scss'],
  imports: [
    ButtonComponent,
    LayoutComponent,
    CardModule,
    CourseSummaryComponent,
    PricePipe,
    NgTemplateOutlet,
    EnrolmentFormComponent,
    GoogleAnalyticsClickEventDirective,
  ],
  providers: [{ provide: PAYMENT_GATEWAY_V2_SERVICE, useClass: FlywireGatewayV2Service }],
})
export class YourInformationComponent implements OnInit {
  private readonly navigationService = inject(NavigationService);
  private readonly store = inject(OnlineQuoteToolStore);
  private readonly leadService = inject(LeadService);
  private readonly paymentGatewayService = inject(PAYMENT_GATEWAY_V2_SERVICE);
  private readonly loadingService = inject(LoadingService);
  private readonly loggerService = inject(LoggerService);
  private readonly breakpointObserverService = inject(BreakpointObserverService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  // Google Analytics event tracking
  protected readonly secureMyPlaceEventName = GoogleAnalyticsEventEnum.QuoteSecureMyPlace;
  protected readonly editCourseEventName = GoogleAnalyticsEventEnum.QuoteCourseEdit;

  protected readonly title = computed(() => this.currentStep()?.title);
  protected readonly subtitle = computed(() => this.currentStep()?.subtitle);
  protected readonly submitted = signal(false);

  protected readonly courseSummary = toSignal(this.store.courseSummary$);
  protected readonly price = computed(() => this.courseSummary()?.grandTotal);
  protected readonly currency = computed(() => this.courseSummary()?.currency);
  protected readonly mobile = toSignal(this.breakpointObserverService.isMobile$);

  protected form: FormGroup | undefined;

  private readonly currentStep = toSignal(this.store.currentStep$);

  ngOnInit(): void {
    this.paymentGatewayService.bootstrapV2();
    this.createForm();
  }

  protected onSecureMyPlace(): void {
    this.submitted.set(true);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loadingService.startLoading($localize`Sending your enrolment...`);

    this.store.selectionForLeads$
      .pipe(
        first(),
        switchMap((selection) => this.leadService.createLead(selection)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: async (leadOutput) => {
          await this.handlePayment(leadOutput);
          this.loadingService.stopLoading();
        },
        error: (error: HttpErrorResponse | Error | unknown) => {
          this.loadingService.stopLoading();
          this.loggerService.error('Failed to create online lead', this.mapErrorForLogging(error));
        },
      });
  }

  protected goBackToBuildYourCourse(): void {
    const step = this.currentStep();
    if (step) {
      this.navigationService.navigateToStep(RouteIds.buildYourCourse);
    }
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      firstName: ['', [Validators.required, Validators.maxLength(80)]],
      lastName: ['', [Validators.required, Validators.maxLength(40)]],
      email: ['', [Validators.required, customEmailValidator()]],
      phoneNumber: ['', [Validators.required, minPhoneNumberLength(MINIMUM_PHONE_LENGTH)]],
      termsAndConditionsConsent: [undefined, Validators.required],
      optOutMarketing: [false],
    });
  }

  private async handlePayment(leadOutput: QuoteLeadOutputV5): Promise<void> {
    const currentStep = this.currentStep();
    if (!currentStep) {
      return;
    }

    this.store.updateState({ quoteId: leadOutput?.quoteId });

    const paymentSessionId = leadOutput?.paymentSessionData?.id;
    if (!paymentSessionId) {
      this.navigationService.navigateToNextStep(currentStep);
      return;
    }

    try {
      const paymentResult = await this.paymentGatewayService.makePaymentV2(paymentSessionId);
      if (paymentResult.success) {
        this.navigationService.navigateToNextStep(currentStep);
      }
    } catch (error: unknown) {
      if (this.isPaymentCancellation(error)) {
        return;
      }

      this.loggerService.error('Error during online payment', this.mapErrorForLogging(error));
      this.navigationService.navigateToNextStep(currentStep);
    }
  }

  private isPaymentCancellation(error: unknown): boolean {
    const paymentError = error as { errors?: Array<{ type?: string }> } | undefined;
    return paymentError?.errors?.[0]?.type === 'cancellation';
  }

  private mapErrorForLogging(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      return `${error.status}: ${error.message}`;
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'Unknown error';
  }
}
